import { create } from 'zustand';
import logger from '../services/loggerService';
import { CONFIG_KEYS } from '../constants/configConstants';
import { userApi } from '../services/api/userApi';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  userId: string | null;
  toggleTheme: () => Promise<void>;
  setTheme: (theme: Theme) => Promise<void>;
  initTheme: (userId: string | null, initialTheme?: Theme) => void;
}

const getStorageKey = (userId: string | null): string => {
  return userId ? `sp:theme:mode:${userId}` : CONFIG_KEYS.THEME_STORE;
};

const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

const resolveTheme = (theme: Theme): 'light' | 'dark' => {
  if (theme === 'system') return getSystemTheme();
  return theme;
};

let systemMediaListener: ((e: MediaQueryListEvent) => void) | null = null;
let debounceTimer: NodeJS.Timeout | null = null;

const syncThemeToBackend = (userId: string, newTheme: Theme) => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(async () => {
    try {
      await userApi.updatePreferences({ themeMode: newTheme });
      logger.debug(`Successfully synced theme "${newTheme}" preference to database for user: ${userId}`, { context: 'useThemeStore' });
    } catch (error) {
      logger.error(`Failed to sync theme preference to database for user: ${userId}`, error, 'useThemeStore.syncThemeToBackend');
    }
  }, 500);
};

const syncSystemListener = (set: any, theme: Theme) => {
  if (typeof window === 'undefined' || !window.matchMedia) return;

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  if (systemMediaListener) {
    mediaQuery.removeEventListener('change', systemMediaListener);
    systemMediaListener = null;
  }

  if (theme === 'system') {
    systemMediaListener = (e: MediaQueryListEvent) => {
      const resolved = e.matches ? 'dark' : 'light';
      set({ resolvedTheme: resolved });
      try {
        localStorage.setItem('sp:theme:resolved', resolved);
      } catch (error) {
        logger.error('Failed to write system resolved theme to localStorage', error, 'useThemeStore.syncSystemListener');
      }
    };
    mediaQuery.addEventListener('change', systemMediaListener);
  }
};

// Initial load of fallback theme
const getInitialTheme = (): Theme => {
  try {
    const fallbackKey = CONFIG_KEYS.THEME_STORE;
    const saved = localStorage.getItem(fallbackKey);
    if (saved === 'light' || saved === 'dark' || saved === 'system') {
      return saved as Theme;
    }
  } catch (error) {
    logger.error('Failed to read default theme from localStorage', error, 'useThemeStore');
  }
  return 'light';
};

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: getInitialTheme(),
  resolvedTheme: resolveTheme(getInitialTheme()),
  userId: null,

  initTheme: (userId, initialTheme) => {
    const key = getStorageKey(userId);
    let themeToApply: Theme = 'light';

    if (initialTheme === 'light' || initialTheme === 'dark' || initialTheme === 'system') {
      themeToApply = initialTheme;
      try {
        localStorage.setItem(key, themeToApply);
      } catch (error) {
        logger.error('Failed to write initial theme to localStorage', error, 'useThemeStore.initTheme');
      }
    } else {
      try {
        const saved = localStorage.getItem(key);
        if (saved === 'light' || saved === 'dark' || saved === 'system') {
          themeToApply = saved as Theme;
        } else {
          // Check for legacy migration from sessionStorage
          const legacyMigration = sessionStorage.getItem('sp:theme:legacy_migration');
          if (legacyMigration === 'light' || legacyMigration === 'dark' || legacyMigration === 'system') {
            themeToApply = legacyMigration as Theme;
            sessionStorage.removeItem('sp:theme:legacy_migration');
            try {
              localStorage.setItem(key, themeToApply);
            } catch (error) {
              logger.error('Failed to write migrated theme to localStorage', error, 'useThemeStore.initTheme');
            }
          } else {
            // If no user-specific theme is saved, fallback to the global default theme
            const fallbackSaved = localStorage.getItem(CONFIG_KEYS.THEME_STORE);
            themeToApply = (fallbackSaved === 'light' || fallbackSaved === 'dark' || fallbackSaved === 'system') 
              ? (fallbackSaved as Theme) 
              : 'light';
          }
        }
      } catch (error) {
        logger.error('Failed to read theme from localStorage', error, 'useThemeStore.initTheme');
      }
    }

    const resolved = resolveTheme(themeToApply);
    syncSystemListener(set, themeToApply);

    set({ userId, theme: themeToApply, resolvedTheme: resolved });
    try {
      localStorage.setItem('sp:theme:resolved', resolved);
    } catch (error) {
      logger.error('Failed to write resolved theme to localStorage', error, 'useThemeStore.initTheme');
    }
    logger.debug(`Initialized theme "${themeToApply}" (resolved: "${resolved}") for user: ${userId || 'anonymous'}`, { context: 'useThemeStore' });
  },

  toggleTheme: async () => {
    const { theme, userId } = get();
    // Circular toggle: light -> dark -> system -> light
    let newTheme: Theme = 'light';
    if (theme === 'light') newTheme = 'dark';
    else if (theme === 'dark') newTheme = 'system';
    
    const key = getStorageKey(userId);

    try {
      localStorage.setItem(key, newTheme);
    } catch (error) {
      logger.error('Failed to write theme toggle to localStorage', error, 'useThemeStore.toggleTheme');
    }

    const resolved = resolveTheme(newTheme);
    syncSystemListener(set, newTheme);

    set({ theme: newTheme, resolvedTheme: resolved });
    try {
      localStorage.setItem('sp:theme:resolved', resolved);
    } catch (error) {
      logger.error('Failed to write resolved theme to localStorage', error, 'useThemeStore.toggleTheme');
    }

    if (userId) {
      syncThemeToBackend(userId, newTheme);
    }
  },

  setTheme: async (newTheme) => {
    const { userId } = get();
    const key = getStorageKey(userId);

    try {
      localStorage.setItem(key, newTheme);
    } catch (error) {
      logger.error('Failed to write theme preference to localStorage', error, 'useThemeStore.setTheme');
    }

    const resolved = resolveTheme(newTheme);
    syncSystemListener(set, newTheme);

    set({ theme: newTheme, resolvedTheme: resolved });
    try {
      localStorage.setItem('sp:theme:resolved', resolved);
    } catch (error) {
      logger.error('Failed to write resolved theme to localStorage', error, 'useThemeStore.setTheme');
    }

    if (userId) {
      syncThemeToBackend(userId, newTheme);
    }
  },
}));
